/**
 * Temperature differential pairs on the pour create wizard's draft rows.
 *
 * Draft rows have no IDs yet, so a pair is stored as an index into the
 * draft list: row i "owns" a pair with row j when i's DiffLocationIndices
 * holds j. A pair is undirected, so the UI reads it from both sides
 * (symmetricPartners) and writes it on one owner (setPartners). The owner
 * matters because the backend drops pairs owned by non-concrete rows (site
 * or humidity loggers).
 *
 * Everything here is pure and never mutates its inputs. Rows whose list
 * does not change keep their identity.
 */

export interface DiffDraftRow {
  DiffLocationIndices: number[];
}

const inRange = (i: number, length: number): boolean => Number.isInteger(i) && i >= 0 && i < length;

const listOf = (row: DiffDraftRow | undefined): number[] => row?.DiffLocationIndices || [];

/** Indices of every row paired with row `idx`, from either side, sorted and
 *  unique. Self references and indices outside the list are ignored. */
export const symmetricPartners = <T extends DiffDraftRow>(rows: ReadonlyArray<T>, idx: number): number[] => {
  if (!inRange(idx, rows.length)) return [];
  const partners = new Set<number>();
  listOf(rows[idx]).forEach((i) => {
    if (i !== idx && inRange(i, rows.length)) partners.add(i);
  });
  rows.forEach((row, i) => {
    if (i !== idx && listOf(row).includes(idx)) partners.add(i);
  });
  return Array.from(partners).sort((a, b) => a - b);
};

/** Replaces every pair touching row `idx` with exactly `nextPartnerIdx`.
 *
 *  Removes `idx` from the other rows' lists and clears `idx`'s own list,
 *  then adds each new pair on `idx` when canOwn(idx) is true, otherwise on
 *  the partner. Self references, duplicates and indices outside the list in
 *  `nextPartnerIdx` are ignored. The caller offers only eligible partners. */
export const setPartners = <T extends DiffDraftRow>(
  rows: ReadonlyArray<T>,
  idx: number,
  nextPartnerIdx: ReadonlyArray<number>,
  canOwn: (i: number) => boolean,
): T[] => {
  if (!inRange(idx, rows.length)) return rows.slice();

  const next = Array.from(new Set(nextPartnerIdx.filter((i) => i !== idx && inRange(i, rows.length)))).sort(
    (a, b) => a - b,
  );
  const ownsAll = canOwn(idx);

  return rows.map((row, i) => {
    const current = listOf(row);
    let updated: number[];
    if (i === idx) {
      updated = ownsAll ? next.slice() : [];
    } else {
      updated = current.filter((j) => j !== idx);
      if (!ownsAll && next.includes(i)) updated.push(idx);
    }
    const same = updated.length === current.length && updated.every((j, k) => j === current[k]);
    return same ? row : { ...row, DiffLocationIndices: updated };
  });
};

/** Removes row `idx` and keeps the other rows' pairs pointing at the same
 *  rows: references to `idx` are dropped and higher indices move down one. */
export const removeRowAndRenumber = <T extends DiffDraftRow>(rows: ReadonlyArray<T>, idx: number): T[] => {
  if (!inRange(idx, rows.length)) return rows.slice();
  return rows
    .filter((_, i) => i !== idx)
    .map((row) => {
      const current = listOf(row);
      if (!current.some((j) => j >= idx)) return row;
      const updated = current.filter((j) => j !== idx).map((j) => (j > idx ? j - 1 : j));
      return { ...row, DiffLocationIndices: updated };
    });
};
